import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ChevronDown,
  FileText,
  GripVertical,
  PanelRightClose,
  PanelRightOpen,
  Sparkles,
} from 'lucide-react';
import { useResizableRightWorkspaceSidebarWidth } from '../../hooks/useResizableRightWorkspaceSidebarWidth';
import { useSheetDialog } from '../../hooks/useSheetDialog';
import {
  RIGHT_WORKSPACE_SIDEBAR_MAX_WIDTH_PX,
  RIGHT_WORKSPACE_SIDEBAR_MIN_WIDTH_PX,
} from './rightWorkspaceSidebarLayout';

export type RightWorkspaceSidebarContext = {
  id: string;
  title: string;
  description?: string;
  accessibleLabel: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  body: React.ReactNode;
};
type Slot = RightWorkspaceSidebarContext;
export type RightWorkspaceSidebarMode = 'open' | 'minimized' | 'closed';
export const RIGHT_WORKSPACE_SIDEBAR_MODE_STORAGE_KEY = 'zunelo_right_workspace_sidebar_mode';

function areNodesEqual(left: React.ReactNode, right: React.ReactNode): boolean {
  if (Object.is(left, right)) return true;
  if (typeof left === 'function' && typeof right === 'function') return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => areNodesEqual(value, right[index]))
    );
  }
  if (!React.isValidElement(left) || !React.isValidElement(right)) return false;
  if (left.type !== right.type || left.key !== right.key) return false;
  const leftProps = left.props as Record<string, unknown>;
  const rightProps = right.props as Record<string, unknown>;
  const leftKeys = Object.keys(leftProps);
  const rightKeys = Object.keys(rightProps);
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every((key) => {
      const leftValue = leftProps[key];
      const rightValue = rightProps[key];
      if (Array.isArray(leftValue) || Array.isArray(rightValue)) {
        return areNodesEqual(leftValue as React.ReactNode, rightValue as React.ReactNode);
      }
      if (React.isValidElement(leftValue) || React.isValidElement(rightValue)) {
        return areNodesEqual(leftValue as React.ReactNode, rightValue as React.ReactNode);
      }
      if (typeof leftValue === 'function' && typeof rightValue === 'function') return true;
      return Object.is(leftValue, rightValue);
    })
  );
}

function areSlotsEqual(left: Slot | undefined, right: Slot): boolean {
  if (!left) return false;
  return (
    left.id === right.id &&
    left.title === right.title &&
    left.description === right.description &&
    left.accessibleLabel === right.accessibleLabel &&
    areNodesEqual(left.icon, right.icon) &&
    areNodesEqual(left.actions, right.actions) &&
    areNodesEqual(left.body, right.body)
  );
}

export function readRightWorkspaceSidebarMode(): RightWorkspaceSidebarMode | null {
  try {
    const value = window.localStorage.getItem(RIGHT_WORKSPACE_SIDEBAR_MODE_STORAGE_KEY);
    return value === 'open' || value === 'minimized' || value === 'closed' ? value : null;
  } catch {
    return null;
  }
}

type RightWorkspaceSidebarControls = {
  mode: RightWorkspaceSidebarMode;
  hasSidebar: boolean;
  minimize: () => void;
  restore: () => void;
  toggle: () => void;
};

type RightWorkspaceSidebarContextValue = RightWorkspaceSidebarControls & {
  registerSlot: (slot: Slot) => () => void;
  updateSlot: (slot: Slot) => void;
  sidebarContent: React.ReactElement | null;
};

type RightWorkspaceSidebarControlsValue = RightWorkspaceSidebarControls & {
  registerSlot: (slot: Slot) => () => void;
  updateSlot: (slot: Slot) => void;
};

const RightWorkspaceSidebarContext = createContext<RightWorkspaceSidebarContextValue | null>(null);

export function useRightWorkspaceSidebar(): RightWorkspaceSidebarControls {
  const context = useContext(RightWorkspaceSidebarContext);
  return (
    context ?? {
      mode: 'open',
      hasSidebar: false,
      minimize: () => {},
      restore: () => {},
      toggle: () => {},
    }
  );
}

export function RightWorkspaceSidebarContent(): React.ReactElement | null {
  const context = useContext(RightWorkspaceSidebarContext);
  return context?.sidebarContent ?? null;
}

export function RightWorkspaceSidebarSlot(slot: Slot): null {
  const sidebar = useContext(RightWorkspaceSidebarContext);
  const registerSlot = sidebar?.registerSlot;
  const updateSlot = sidebar?.updateSlot;
  const slotRef = useRef(slot);
  slotRef.current = slot;
  useEffect(() => {
    return registerSlot?.(slotRef.current);
  }, [registerSlot, slot.id]);
  useEffect(() => {
    updateSlot?.(slot);
  }, [slot, updateSlot]);
  return null;
}

export function RightWorkspaceSidebarDummies({
  enabled,
}: {
  enabled: boolean;
}): React.ReactElement | null {
  // Demo-only click counter: gives the "Action" header button a documented,
  // observable effect instead of being a no-op — see
  // openspec/changes/provider-item-interaction-ux (no inert demo controls).
  const [actionClicks, setActionClicks] = useState(0);
  if (!enabled) return null;
  return (
    <>
      <RightWorkspaceSidebarSlot
        id="dummy-overview"
        title="Dummy sidebar widget · Overview"
        description="Demo only · icon and description"
        accessibleLabel="Dummy sidebar widget overview"
        icon={<Sparkles size={16} />}
        body={<p data-testid="sidebar-dummy-overview">Example content for visual inspection.</p>}
      />
      <RightWorkspaceSidebarSlot
        id="dummy-action"
        title="Dummy sidebar widget · Action"
        description="Demo only · header action"
        accessibleLabel="Dummy sidebar widget action"
        actions={
          <button
            type="button"
            data-testid="sidebar-dummy-action-button"
            onClick={() => setActionClicks((n) => n + 1)}
          >
            Action ({actionClicks})
          </button>
        }
        body={
          <p data-testid="sidebar-dummy-action">
            Short example content. Clicked {actionClicks} time
            {actionClicks === 1 ? '' : 's'}.
          </p>
        }
      />
      <RightWorkspaceSidebarSlot
        id="dummy-long-title"
        title="Dummy sidebar widget · A deliberately long title to test wrapping"
        accessibleLabel="Dummy sidebar widget with long title"
        icon={<FileText size={16} />}
        body={<p data-testid="sidebar-dummy-long">No card background or nested border.</p>}
      />
    </>
  );
}

export function RightWorkspaceSidebarProvider({
  isCompact,
  fallback,
  header,
  renderSidebar = true,
  closeOnNavigate,
  closeOnLeftDrawerOpen,
  children,
}: {
  isCompact: boolean;
  fallback?: React.ReactNode;
  header?: React.ReactNode;
  renderSidebar?: boolean;
  /** Changes value (e.g. the active view) close the compact sheet — only one
   * full-screen mobile overlay makes sense open at a time. */
  closeOnNavigate?: unknown;
  closeOnLeftDrawerOpen?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const [slots, setSlots] = useState<Record<string, Slot>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [mode, setMode] = useState<RightWorkspaceSidebarMode>(
    () => readRightWorkspaceSidebarMode() ?? (isCompact ? 'closed' : 'open'),
  );
  const { width, isResizing, onResizePointerDown, onResizeKeyDown } =
    useResizableRightWorkspaceSidebarWidth();
  const modules = Object.values(slots);
  const hasModules = modules.length > 0;
  const hasSidebar = hasModules || Boolean(fallback);
  const label = modules[0]?.accessibleLabel ?? 'Panel de contexto';
  const registerSlot = useCallback((slot: Slot) => {
    setSlots((current) => {
      if (areSlotsEqual(current[slot.id], slot)) return current;
      return { ...current, [slot.id]: slot };
    });
    return () =>
      setSlots((current) => {
        if (!current[slot.id]) return current;
        const next = { ...current };
        delete next[slot.id];
        return next;
      });
  }, []);
  const updateSlot = useCallback((slot: Slot) => {
    setSlots((current) => {
      if (!current[slot.id] || areSlotsEqual(current[slot.id], slot)) return current;
      return { ...current, [slot.id]: slot };
    });
  }, []);
  const minimize = useCallback(() => setMode('minimized'), []);
  const restore = useCallback(() => setMode('open'), []);
  const toggle = useCallback(
    () => setMode((current) => (current === 'minimized' ? 'open' : 'minimized')),
    [],
  );
  const controls = useMemo<RightWorkspaceSidebarControlsValue>(
    () => ({
      mode,
      hasSidebar,
      registerSlot,
      updateSlot,
      minimize,
      restore,
      toggle,
    }),
    [hasSidebar, minimize, mode, registerSlot, restore, toggle, updateSlot],
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(RIGHT_WORKSPACE_SIDEBAR_MODE_STORAGE_KEY, mode);
    } catch {
      // The sidebar remains usable when storage is unavailable.
    }
  }, [mode]);

  // Only one full-screen mobile overlay should ever be open at a time:
  // navigating away, or opening the left drawer, closes this sheet. Skip the
  // very first run so mounting doesn't immediately stomp the restored mode.
  const skipNextNavigateClose = useRef(true);
  useEffect(() => {
    if (skipNextNavigateClose.current) {
      skipNextNavigateClose.current = false;
      return;
    }
    if (isCompact) setMode('minimized');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeOnNavigate]);

  useEffect(() => {
    if (isCompact && closeOnLeftDrawerOpen) setMode('minimized');
  }, [isCompact, closeOnLeftDrawerOpen]);

  const sidebarContent = hasSidebar ? (
    isCompact ? (
      <>
        {/* Floating trigger, reachable near the thumb — in addition to (not
            instead of) the header's own toggle (RightWorkspaceSidebarToggle),
            which mirrors desktop exactly. Both drive the same mode state. */}
        <button
          type="button"
          data-testid="right-workspace-sidebar-open"
          className="z-right-workspace-sidebar-open-rail z-right-workspace-sidebar-rail-button"
          aria-label={mode === 'open' ? `Cerrar ${label}` : `Abrir ${label}`}
          aria-expanded={mode === 'open'}
          onClick={() => (mode === 'open' ? controls.minimize() : controls.restore())}
        >
          {mode === 'open' ? (
            <PanelRightClose size={18} strokeWidth={1.8} aria-hidden="true" />
          ) : (
            <PanelRightOpen size={18} strokeWidth={1.8} aria-hidden="true" />
          )}
        </button>
        <CompactSidebarSheet
          isOpen={mode === 'open'}
          label={label}
          modules={modules}
          fallback={fallback}
          expanded={expanded}
          onToggleModule={(id) =>
            setExpanded((current) => ({ ...current, [id]: current[id] === false }))
          }
          onClose={controls.minimize}
        />
      </>
    ) : (
      <aside
        data-testid="right-workspace-sidebar"
        className="z-right-workspace-sidebar"
        data-minimized={mode === 'minimized' ? 'true' : 'false'}
        aria-label={label}
        style={
          mode === 'minimized'
            ? undefined
            : {
                width: `${width}px`,
                flex: `0 0 ${width}px`,
                transition: isResizing ? 'none' : 'width 0.15s ease, flex-basis 0.15s ease',
              }
        }
      >
        {mode !== 'minimized' ? (
          <div
            data-testid="right-workspace-sidebar-resize-handle"
            className="z-right-workspace-sidebar-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={width}
            aria-valuemin={RIGHT_WORKSPACE_SIDEBAR_MIN_WIDTH_PX}
            aria-valuemax={RIGHT_WORKSPACE_SIDEBAR_MAX_WIDTH_PX}
            aria-label="Redimensionar panel contextual"
            title="Arrastra para cambiar el ancho"
            tabIndex={0}
            data-resizing={isResizing ? 'true' : 'false'}
            onPointerDown={onResizePointerDown}
            onKeyDown={onResizeKeyDown}
          >
            <GripVertical size={28} strokeWidth={2} aria-hidden="true" />
          </div>
        ) : null}
        {mode === 'minimized' ? (
          <div className="z-right-workspace-sidebar-rail">
            {hasModules ? (
              <div className="z-right-workspace-sidebar-rail-modules">
                {modules.map((module) => (
                  <button
                    key={module.id}
                    type="button"
                    className="z-right-workspace-sidebar-rail-button"
                    data-testid={`right-workspace-sidebar-rail-${module.id}`}
                    data-tooltip={module.title}
                    aria-label={`Abrir ${module.title}`}
                    title={module.title}
                    onClick={controls.restore}
                  >
                    {module.icon ?? <Sparkles size={16} strokeWidth={1.8} aria-hidden="true" />}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="z-right-workspace-sidebar-modules">
            {hasModules ? (
              modules.map((module) => (
                <SidebarModule
                  key={module.id}
                  module={module}
                  expanded={expanded[module.id] !== false}
                  onToggle={() =>
                    setExpanded((current) => ({
                      ...current,
                      [module.id]: current[module.id] === false,
                    }))
                  }
                />
              ))
            ) : fallback ? (
              <div className="z-right-workspace-sidebar-fallback">{fallback}</div>
            ) : null}
          </div>
        )}
      </aside>
    )
  ) : null;

  return (
    <RightWorkspaceSidebarContext.Provider value={{ ...controls, sidebarContent }}>
      {header ? (
        <div className="z-workspace-shell">
          {header}
          <div className="z-workspace-shell-body">
            {children}
            {renderSidebar ? sidebarContent : null}
          </div>
        </div>
      ) : renderSidebar ? (
        <>
          {children}
          {sidebarContent}
        </>
      ) : (
        <div className="z-workspace-shell">{children}</div>
      )}
    </RightWorkspaceSidebarContext.Provider>
  );
}

/**
 * Mobile presentation of the contextual sidebar. Unlike the desktop rail it is a
 * modal full-screen sheet, so it needs its own dismissal affordances: a titled
 * header with a close button, Escape, and a background scroll lock.
 */
function CompactSidebarSheet({
  isOpen,
  label,
  modules,
  fallback,
  expanded,
  onToggleModule,
  onClose,
}: {
  isOpen: boolean;
  label: string;
  modules: Slot[];
  fallback?: React.ReactNode;
  expanded: Record<string, boolean>;
  onToggleModule: (id: string) => void;
  onClose: () => void;
}): React.ReactElement {
  // Always mounted, visibility driven by the "data-open" attribute + a CSS
  // transition (transform), the same pattern the left drawer's z-sidebar
  // uses — that's what gives it a real, reliable, bidirectional open *and*
  // close animation instead of a mount-triggered entrance with no exit.
  const sheetRef = useSheetDialog<HTMLElement>({
    isOpen,
    onClose,
  });

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  return (
    <aside
      ref={sheetRef as React.RefObject<HTMLElement>}
      className="z-right-workspace-sidebar z-right-workspace-sidebar--sheet"
      data-testid="right-workspace-sidebar-sheet"
      data-open={isOpen ? 'true' : 'false'}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      aria-hidden={isOpen ? undefined : true}
      tabIndex={-1}
      {...(isOpen ? {} : ({ inert: '' } as React.HTMLAttributes<HTMLElement>))}
    >
      {/* No page-level title bar or close button here — desktop's dock
          doesn't have one either (each module carries its own header), and
          opening/closing is the header's toggle button, not a control
          duplicated inside the sheet. */}
      <div className="z-right-workspace-sidebar-modules">
        {modules.length > 0 ? (
          modules.map((module) => (
            <SidebarModule
              key={module.id}
              module={module}
              expanded={expanded[module.id] !== false}
              onToggle={() => onToggleModule(module.id)}
            />
          ))
        ) : fallback ? (
          <div className="z-right-workspace-sidebar-fallback">{fallback}</div>
        ) : null}
      </div>
    </aside>
  );
}

function SidebarHeader({ context }: { context: RightWorkspaceSidebarContext }): React.ReactElement {
  return (
    <div className="z-right-workspace-sidebar-header">
      <span className="z-right-workspace-sidebar-module-icon" aria-hidden="true">
        {context.icon ?? <Sparkles size={17} strokeWidth={1.8} />}
      </span>
      <strong>{context.title}</strong>
      {context.description ? (
        <span className="z-right-workspace-sidebar-description">{context.description}</span>
      ) : null}
    </div>
  );
}

function SidebarModule({
  module,
  expanded,
  onToggle,
}: {
  module: Slot;
  expanded: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const moduleBodyId = `right-workspace-sidebar-module-${module.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  return (
    <section className="z-right-workspace-sidebar-module" data-module-id={module.id}>
      <div className="z-right-workspace-sidebar-module-header">
        <button
          type="button"
          className="z-right-workspace-sidebar-module-toggle"
          aria-expanded={expanded}
          aria-controls={moduleBodyId}
          onClick={onToggle}
        >
          <ChevronDown
            className="z-right-workspace-sidebar-chevron"
            size={18}
            strokeWidth={1.8}
            aria-hidden="true"
          />
          <SidebarHeader context={module} />
        </button>
        {module.actions ? (
          <div className="z-right-workspace-sidebar-module-actions">{module.actions}</div>
        ) : null}
      </div>
      {expanded ? (
        <div id={moduleBodyId} className="z-right-workspace-sidebar-module-body">
          {module.body}
        </div>
      ) : null}
    </section>
  );
}
